
import React from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Coins } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface ListingCreditsInfoProps {
  editMode: boolean;
  hasNoCreditsLeft: boolean;
  showStockWarning: boolean;
  stockItemsLimit: number;
  totalCredits: number;
  freeListingsRemaining: number;
  purchasedListingsCredits: number;
}

const ListingCreditsInfo = ({
  editMode,
  hasNoCreditsLeft,
  showStockWarning,
  stockItemsLimit,
  totalCredits,
  freeListingsRemaining,
  purchasedListingsCredits
}: ListingCreditsInfoProps) => {
  const navigate = useNavigate();

  // Render credits warning - only show when total credits are zero
  const renderCreditsWarning = () => {
    if (!editMode && hasNoCreditsLeft) {
      return (
        <Alert variant="destructive" className="mb-4">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Nuk ka kredite në dispozicion</AlertTitle>
          <AlertDescription>
            Ju nuk keni më kredite në dispozicion. Ju lutemi blini kredite për të krijuar një njoftim të ri.
            <Button 
              variant="outline" 
              size="sm" 
              className="mt-2"
              onClick={() => navigate('/coins')}
            >
              Blini kredite
            </Button>
          </AlertDescription>
        </Alert>
      );
    }
    return null;
  };
  
  // Render stock items warning
  const renderStockWarning = () => {
    if (showStockWarning) {
      return (
        <Alert variant="destructive" className="mb-4">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Lager-Limit überschritten</AlertTitle>
          <AlertDescription>
            Ihr Plan erlaubt maximal {stockItemsLimit} Lagerartikel. Bitte abonnieren Sie einen höheren Plan oder reduzieren Sie die Anzahl der Lagerartikel.
          </AlertDescription>
        </Alert>
      );
    }
    return null;
  };

  const renderCreditsInfo = () => {
    if (!editMode && totalCredits > 0) {
      return (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md flex items-center">
          <Coins className="h-5 w-5 text-green-500 mr-2" />
          <span>
            Kredite në dispozicion: <strong>{totalCredits}</strong> (Falas: {freeListingsRemaining}, Blerë: {purchasedListingsCredits})
          </span>
        </div>
      );
    }
    return null;
  };

  return (
    <>
      {renderCreditsWarning()}
      {renderStockWarning()}
      {renderCreditsInfo()}
    </>
  );
};

export default ListingCreditsInfo;
